/**
 * The rows and controls the search dropdown is built from, lifted out of `qwib-search.tsx` so the
 * box's own file holds only the box: the term, the queries, the bands and the keyboard.
 *
 * Every export here is a leaf — `{ data, onSelect } -> JSX`, closing over nothing in `QwibSearch` —
 * which is what makes the seam a clean one. It is also where a new band's row goes, so the
 * orchestrating file grows by a query and a `map` rather than by a component each time.
 */
import type {
  InboxAgencySearchResultFragment,
  InboxAgentSearchResultFragment,
  InboxSearchSuggestionConversationFragment,
} from "@qw/graphql";
import { Button, CommandItem, CommandList, Icon } from "@qw/ui";
import { Children, type PropsWithChildren } from "react";
import type { BookInsured } from "@/book/use-book-insureds";
import { getConversationLinkSender } from "@/inbox/conversation-link/conversation-link-sender";
import { SEARCH_OPERATOR_COPY, type SearchOperator } from "@/search/search-operators";

/**
 * The popover under the box. Both of the dropdown's jobs render through this one so the
 * `onMouseDown` cannot diverge between them: it is what stops a click on a row from losing to the
 * input's `onBlur`, and so what keeps the caret in the box and the popover open.
 *
 * One ungrouped list on the results side: the bands are an order, not sections. Headings would
 * announce a band that is still loading as empty, which is the failure the combined list exists to
 * avoid.
 *
 * It owns "an empty popover under a focused field is a hole" rather than leaving each caller to
 * restate it. `Children.toArray` drops the `false`s that gated-out halves render as, so a dropdown
 * whose halves all declined to render is one with nothing in it — which means a caller adds a half
 * by writing its gate once, instead of writing it again in a condition wrapping the whole list.
 */
export const SearchDropdown = ({ children }: PropsWithChildren) => {
  if (Children.toArray(children).length === 0) {
    return null;
  }

  return (
    <CommandList
      onMouseDown={(event) => event.preventDefault()}
      className="bg-popover text-popover-foreground absolute inset-x-0 top-9 z-50 max-h-96 rounded-lg border p-1 shadow-md"
    >
      {children}
    </CommandList>
  );
};

/**
 * The pills above an empty box, Gmail's row with the entries we can honestly fill: the ways the box
 * can be narrowed *to* something.
 *
 * Plain buttons, not `CommandItem`s. cmdk's arrow keys move vertically, so pills laid out
 * horizontally would navigate sideways while feeling vertical; Tab is the axis a row wants and the
 * axis Gmail's row uses. That is also why the row sits above the terms rather than under them —
 * arrowing down a list of searches should not walk into a control that does not answer to arrows.
 */
export const SearchOfferRow = ({ children }: PropsWithChildren) => (
  // No padding of its own, so the pills sit at the list's own `p-1` inset — the same 4px a
  // `CommandItem`'s `bg-accent` highlight sits at. A pill is a filled box like that highlight, and
  // it is the two boxes' edges the eye lines up, not their text. The gap under the row belongs to
  // the separator, which is what keeps this flush to the list's bottom padding when there are no
  // recents to divide from.
  <div
    className="flex gap-1"
    // A focused control owns its own keys. Without this, Enter on a pill reaches cmdk's root
    // handler, which fires whatever row it has highlighted — so activating a pill would also run a
    // search. `stopPropagation` and not `preventDefault`: the browser synthesises the button's
    // click from this same Enter, and preventing the default would stop the pill working at all.
    onKeyDown={(event) => event.stopPropagation()}
  >
    {children}
  </div>
);

/**
 * Where the unlinked scope gets turned on. It replaces a checkbox behind a `filter_list` icon-menu
 * that no broker ever found — four weeks live, not one toggle — so being visible on focus is the
 * whole point. It costs no query either: `skipSuggestions` needs three characters, so an empty box
 * renders this without either suggestion query firing.
 */
export const UnlinkedScopeOffer = ({ onAdd }: { onAdd: () => void }) => (
  <Button variant="secondary" size="xs" onClick={onAdd}>
    <Icon icon="link_off" />
    Only unlinked email
  </Button>
);

/**
 * Where an operator is discovered. It is the discoverability half of the parser and ships with it,
 * not after: a correct-but-invisible operator is the **Only Unlinked Emails** checkbox again — four
 * weeks live, not one toggle — and typed syntax is *less* findable than a checkbox, not more.
 *
 * Title case rather than the literal `subject:`, which reads as a fragment on a pill — the box is
 * the better place to learn the syntax from, and `handleAddOperator` is what puts it there.
 *
 * The `aria-label` is not redundant with that text. `Icon` renders its glyph name as text, so the
 * computed name would otherwise be "subject Subject", and "Subject" alone does not say that
 * pressing it starts a search.
 */
export const OperatorOffer = ({
  operator,
  onAdd,
}: {
  operator: SearchOperator;
  onAdd: (operator: SearchOperator) => void;
}) => {
  const { icon, label, dimension } = SEARCH_OPERATOR_COPY[operator];

  return (
    <Button variant="secondary" size="xs" aria-label={`Search by ${dimension}`} onClick={() => onAdd(operator)}>
      <Icon icon={icon} />
      {label}
    </Button>
  );
};

/**
 * A term the broker already used, offered back. Selecting it runs that search down the same path
 * Enter takes, so a recent row and a retyped term land in the same place.
 *
 * The remove control sits *inside* the row because the row is a `CommandItem` and cmdk owns the
 * whole rectangle — there is no beside. That makes `stopPropagation` load-bearing rather than
 * defensive: without it the click reaches the item's handler too, and removing an entry would run
 * the search it was removing on the way out.
 */
export const RecentSearchItem = ({
  term,
  onSelect,
  onRemove,
}: {
  term: string;
  onSelect: (term: string) => void;
  onRemove: (term: string) => void;
}) => (
  <CommandItem value={`recent-${term}`} onSelect={() => onSelect(term)}>
    <Icon icon="history" className="text-muted-foreground text-sm" />
    <span className="truncate">{term}</span>
    <button
      type="button"
      aria-label={`Remove ${term} from recent searches`}
      className="ml-auto flex size-4 shrink-0 cursor-pointer opacity-50"
      // Deliberately a tab stop, unlike the two scope chips. Those are `tabIndex={-1}` for a reason
      // that does not apply here — they render *before* the field, so a stop on them sits in front
      // of the box on every scoped route — and they keep a keyboard path anyway, in the Backspace
      // `handleKeyDown` owns. This control has neither: it renders after the input, and it is the
      // only way to remove a term at all now that there is no clear-all pill, so taking it out of
      // the tab order would make forgetting a search mouse-only. The row above is arrow-navigable;
      // this is how it gets deleted. The cost is bounded by `RECENT_SEARCH_LIMIT`, and the `Clear
      // search` ✕ on the input is the same trade already made.
      //
      // `onMouseDown` still preventDefaults for the same reason the chips do: letting this take the
      // caret would pull focus out of the box mid-search, and the row exists to put a search into it.
      onMouseDown={(event) => event.preventDefault()}
      // The keyboard's version of the `onClick` stop below, and it guards something worse. cmdk's
      // highlight follows arrows and the pointer, not focus, so an Enter that reached its root
      // would run whichever row is highlighted — not necessarily the row being removed.
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onRemove(term);
      }}
    >
      <Icon icon="close" className="my-auto text-sm" />
    </button>
  </CommandItem>
);

export const InsuredSuggestion = ({
  insured,
  onSelect,
}: {
  insured: BookInsured;
  onSelect: (insuredId: string) => void;
}) => (
  <CommandItem value={`insured-${insured.id}`} onSelect={() => onSelect(insured.id)}>
    <Icon icon="domain" className="text-muted-foreground text-sm" />
    <span className="truncate">{insured.name}</span>
  </CommandItem>
);

/**
 * An agent the broker has corresponded with, offered as a scope rather than as a destination.
 *
 * Thin on purpose — a name and who they work for. The agency is not decoration: it is what tells the
 * two rows apart whenever the term filled both slots, which is the case `AGENT_SLOTS` exists
 * for. Everything else about them belongs on the card above the mail list (B-1846), where there is
 * room for it and where the broker has already picked which agent they meant.
 *
 * The address is the fallback name rather than a second line. `first_name` and `last_name` are
 * non-null columns but not non-empty ones, and this search matches on the address too, so a row can
 * be reached by an agent it cannot name — and an unlabelled row is worse than a technical one.
 */
export const AgentSuggestion = ({
  agent,
  onSelect,
}: {
  agent: InboxAgentSearchResultFragment;
  onSelect: (agentId: string) => void;
}) => (
  <CommandItem value={`agent-${agent.id}`} onSelect={() => onSelect(agent.id)}>
    <Icon icon="person" className="text-muted-foreground text-sm" />
    <span className="truncate">{`${agent.firstName} ${agent.lastName}`.trim() || agent.email}</span>
    {agent.agencyName && (
      <span className="text-muted-foreground ml-auto shrink-0 truncate pl-2">{agent.agencyName}</span>
    )}
  </CommandItem>
);

/**
 * An agency the broker has corresponded with, offered as a scope rather than as a destination —
 * the same trade {@link AgentSuggestion} makes, one level up.
 *
 * The counts are the row's whole argument for being clicked, which is why they are on it and the
 * agent row carries no number at all. The scope they open is usually small: across the 31 brokers
 * with 20 or more open accounts, the median agency scope holds one open account and 56.3% hold
 * exactly one. A row that said only the name would be asking the broker to find that out by
 * navigating, and a rail that collapses to a single line reads as broken.
 *
 * Both counts are the caller's own — accounts in their book under the rail's `showExpired`, agents
 * their mailboxes have actually written to — never the agency's platform-wide totals. Zero accounts
 * renders as zero rather than being hidden: an agency the broker emails but holds no accounts for
 * is a legitimate result, and the one where the number is doing the most work.
 */
export const AgencySuggestion = ({
  agency,
  onSelect,
}: {
  agency: InboxAgencySearchResultFragment;
  onSelect: (agencyId: string) => void;
}) => (
  <CommandItem value={`agency-${agency.id}`} onSelect={() => onSelect(agency.id)}>
    <Icon icon="corporate_fare" className="text-muted-foreground text-sm" />
    <span className="truncate">{agency.name}</span>
    <span className="text-muted-foreground ml-auto shrink-0 pl-2">
      {`${countOf(agency.accountCount, "account")} · ${countOf(agency.agentCount, "agent")}`}
    </span>
  </CommandItem>
);

const countOf = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

export const ConversationSuggestion = ({
  conversation,
  currentUserEmail,
  onSelect,
}: {
  conversation: InboxSearchSuggestionConversationFragment;
  currentUserEmail?: string | null;
  onSelect: (conversationId: string) => void;
}) => {
  const { primary } = getConversationLinkSender({
    currentUserEmail,
    lastMessageFrom: conversation.lastMessageFrom,
    participants: conversation.participants,
  });

  return (
    <CommandItem value={`conversation-${conversation.id}`} onSelect={() => onSelect(conversation.id)}>
      <Icon icon="mail" className="text-muted-foreground text-sm" />
      <span className="truncate">{conversation.subject}</span>
      <span className="text-muted-foreground ml-auto shrink-0 truncate pl-2">{primary.fullName}</span>
    </CommandItem>
  );
};
